import { TRACING_GAME_TEXTURE_KEYS } from '../assets';

import type Phaser from 'phaser';
import type { TracingGameSoundController } from './TracingGameSoundController';

type VictorySoundEffects = Pick<TracingGameSoundController, 'playVictory' | 'startBarFill' | 'stopBarFill'>;

type VictoryPresentationConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	gameplayContainer: Phaser.GameObjects.Container;
	width: number;
	height: number;
	soundEffects: VictorySoundEffects;
};

const CONFETTI_WIDTH = 700;
const CONFETTI_COLORS = ['#B8E986', '#FF59EF', '#ED3030', '#4D300E', '#59EEFF', '#36A1FF', '#FBFD02', '#FFFFFF'];
const CONFETTI = {
	duration: 1800,
	lifespan: 2300,
	frequency: 45,
	quantity: 3,
	speedX: { min: -150, max: 150 },
	speedY: { min: 300, max: 400 },
	gravityY: 280,
	scaleX: { min: 0.6, max: 0.9 },
	scaleY: { min: 0.6, max: 0.9 },
	rotate: {
		onEmit: (particle?: Phaser.GameObjects.Particles.Particle) => {
			if (!particle) return 0;
			const rotation = particle.data.rotate!;
			rotation.min = Math.random() * 360;
			rotation.max = rotation.min + (Math.random() * 1440 - 720);
			return rotation.min;
		},
		onUpdate: (particle: Phaser.GameObjects.Particles.Particle, _key: string, progress: number) => {
			const { min, max } = particle.data.rotate!;
			return min + (max - min) * progress;
		},
	},
	alpha: { start: 1, end: 0 },
};

const VICTORY_SIZE = 560;
const REWARD_LAYOUT = { count: 10, size: 100, spacing: 24, bottomInset: 180 };
const ANIMATION = {
	gameplayFade: { duration: 300, ease: 'Sine.Out' },
	victory: { delay: 350, duration: 900, ease: 'Back.Out', easeParams: [1.2] },
	rewards: { duration: 350, ease: 'Back.Out', easeParams: [1.5] },
	stagger: 80,
	fill: { duration: 1400, ease: 'Sine.InOut' },
	completeDelay: 2000,
};

export class VictoryPresentation {
	private readonly container: Phaser.GameObjects.Container;
	private sequence?: Phaser.Tweens.TweenChain;
	private gameplayFade?: Phaser.Tweens.Tween;

	constructor(private readonly config: VictoryPresentationConfig) {
		this.container = config.scene.add.container(0, 0).setDepth(1);
		config.parent.add(this.container);
	}

	play(start: Phaser.Types.Math.Vector2Like, score: number, onComplete: () => void) {
		const { scene, width, height, gameplayContainer } = this.config;
		this.gameplayFade = scene.tweens.add({
			targets: gameplayContainer,
			alpha: 0,
			...ANIMATION.gameplayFade,
			onComplete: () => {
				this.gameplayFade = undefined;
			},
		});
		const victory = scene.add.image(start.x, start.y, TRACING_GAME_TEXTURE_KEYS.victory);
		this.container.add(victory);

		const { count, size, spacing, bottomInset } = REWARD_LAYOUT;
		const step = size + spacing;
		const fills: Phaser.GameObjects.Image[] = [];
		const rewards = Array.from({ length: count }, (_, index) => {
			const reward = scene.add.container(width / 2 + (index - (count - 1) / 2) * step, height - bottomInset);
			const background = scene.add.image(0, 0, TRACING_GAME_TEXTURE_KEYS.rewardElementBackground);
			const fill = scene.add.image(0, 0, TRACING_GAME_TEXTURE_KEYS.rewardElement);
			for (const image of [background, fill]) {
				image.setScale(size / Math.max(image.width, image.height));
			}
			fill.setCrop(0, 0, 0, fill.height);
			fills.push(fill);
			reward.add([background, fill]).setScale(0);
			this.container.add(reward);
			return reward;
		});

		const confetti = this.createConfetti();
		const progress = { value: 0 };
		const targetFill = (Math.max(0, Math.min(100, score)) / 100) * count;
		this.sequence = scene.tweens.chain({
			completeDelay: ANIMATION.completeDelay,
			tweens: [
				{
					targets: victory,
					x: width / 2,
					y: height / 2,
					scale: VICTORY_SIZE / Math.max(victory.width, victory.height),
					...ANIMATION.victory,
					onComplete: () => {
						confetti.start();
						this.config.soundEffects.playVictory();
					},
				},
				{
					targets: rewards,
					scale: 1,
					delay: scene.tweens.stagger(ANIMATION.stagger, {}),
					...ANIMATION.rewards,
				},
				{
					targets: progress,
					value: targetFill,
					...ANIMATION.fill,
					onStart: () => {
						if (targetFill > 0) this.config.soundEffects.startBarFill();
					},
					onUpdate: () => {
						fills.forEach((fill, index) => {
							const fraction = Math.max(0, Math.min(1, progress.value - index));
							fill.setCrop(0, 0, fill.width * fraction, fill.height);
						});
						if (progress.value >= targetFill) this.config.soundEffects.stopBarFill();
					},
				},
			],
			onComplete: () => {
				this.sequence = undefined;
				onComplete();
			},
		});
	}

	private createConfetti() {
		const { scene, width } = this.config;
		const key = TRACING_GAME_TEXTURE_KEYS.confettiColors;
		// Canvas particles ignore tint, so cache colored frames of the white source image.
		if (!scene.textures.exists(key)) {
			const source = scene.textures.get(TRACING_GAME_TEXTURE_KEYS.confetti).getSourceImage();
			const texture = scene.textures.createCanvas(key, source.width * CONFETTI_COLORS.length, source.height)!;
			const context = texture.getContext();
			CONFETTI_COLORS.forEach((color, index) => {
				const x = index * source.width;
				context.globalCompositeOperation = 'source-over';
				context.drawImage(source as HTMLImageElement, x, 0);
				context.globalCompositeOperation = 'source-atop';
				context.fillStyle = color;
				context.fillRect(x, 0, source.width, source.height);
				texture.add(index, 0, x, 0, source.width, source.height);
			});
			context.globalCompositeOperation = 'source-over';
			texture.refresh();
		}
		const emitter = scene.add.particles(width / 2, -40, key, {
			...CONFETTI,
			emitting: false,
			x: { min: -CONFETTI_WIDTH / 2, max: CONFETTI_WIDTH / 2 },
			frame: CONFETTI_COLORS.map((_, index) => index),
		});
		this.container.add(emitter);
		return emitter;
	}

	destroy() {
		this.config.soundEffects.stopBarFill();
		this.sequence?.destroy();
		this.sequence = undefined;
		this.gameplayFade?.remove();
		this.gameplayFade = undefined;
		this.config.gameplayContainer.setAlpha(1);
		this.container.destroy();
	}
}
